import {
  DocumentNode,
  gql,
  LazyQueryResultTuple,
  QueryResult,
  useLazyQuery,
  useMutation,
  useQuery,
} from '@apollo/client'
import { DatableLog, DatableTrace, Pipeline, PipelineParams, QueryParams, SimulateEventParams } from '@ui/interfaces'

export const LinechartAndPipelineQL = gql(`
  query LinechartAndPipeline($pipelineParams: PipelineParams, $queryParams: QueryParams) {
    pipeline(pipelineParams: $pipelineParams, queryParams: $queryParams) {
      lineChart {
        bucketSize
        timeseries {
          timestamp
          volume
        }
      }
    }
    telemetry(queryParams: $queryParams) {
      totals {
        count
      }
    }
  }
`)

export const SimulateEventPipelineQL = gql(`
  query SimulateEventPipeline($simulateEventParams: SimulateEventParams) {
    pipeline {
      simulateEvent(simulateEventParams: $simulateEventParams) {
        selectedEvent {
          input
          output
          consoleLog
          errorLog
        }
        events {
          input
          output
          consoleLog
          errorLog
        }
      }
    }
  }
`)

const GetPipelineListQL = gql(`
  query GetPipelineList {
    pipelineList {
      enabled
      id
      numberOfSlices
      name
      slices {
        type
        slug
      }
      doesPipelineHaveErrors
    }
  }
`)

const GetLoafQL: DocumentNode = gql(`
  query GetLoaf($loafId: Int!) {
    getLoaf(loaf_id: $loafId) {
      id
      enabled
      name
      firehose {
        size {
          span
          log
          metric
        }
      }
      slices {
        id
        code
        parentSliceId
        type
        description
        slug
        size {
          span
          log
          metric
        }
        status {
          logsEnabled
          spansEnabled
          metricsEnabled
          logsStatus
          spansStatus
          metricsStatus
        }
      }
    }
  }
`)

const GetLoafAndAssignedSourcesQL: DocumentNode = gql(`
  query GetLoafAndAssignedSources($loafId: Int!) {
    getLoaf(loaf_id: $loafId) {
      id
      enabled
      name
      slices {
        id
        code
        parentSliceId
        type
        description
        slug
        size {
          span
          log
          metric
        }
        status {
          logsEnabled
          spansEnabled
          metricsEnabled
          logsStatus
          spansStatus
          metricsStatus
        }
      }
      assignedSources {
        id
        name
        type
      }
    }
  }
`)

const GetLoafWithAssignedSourcesAndDestinationsQL: DocumentNode = gql(`
  query GetWithLoafWithAssignedSourcesAndDestinations($loafId: Int!) {
    getLoaf(loaf_id: $loafId) {
      id
      enabled
      name
      firehose {
        size {
          span
          log
          metric
        }
      }
      slices {
        id
        code
        parentSliceId
        type
        description
        slug
        size {
          span
          log
          metric
        }
        status {
          logsEnabled
          spansEnabled
          metricsEnabled
          logsStatus
          spansStatus
          metricsStatus
        }
        destination {
          id
          name
          formData
          template {
            id
            name
            slug
            description
            type
          }
        }
      }
      assignedSources {
        id
        name
        type
      }
    }
  }
`)

const CreateLoafAndSlicesMutationQL = gql(`
mutation CreateLoafAndSlices($loaf: CreateLoafInput!, $slices: [PartialSliceInput]!) {
  createLoafAndSlices(loaf: $loaf, slices: $slices) {
    enabled
    id
    name
    slices {
      id
      type
      oldId
    }
  }
}
`)

const CreateLoafSlicesAndAssignedSourcesMutationQL = gql(`
  mutation CreateLoafSlicesAndAssignedSources($loaf: CreateLoafInput!, $slices: [PartialSliceInput]!, $assignedSourceIds: [Int!]!) {
    createLoafAndSlices(loaf: $loaf, slices: $slices, assignedSourceIds: $assignedSourceIds) {
      enabled
      id
      name
      slices {
        id
        type
        oldId
      }
    }
  }
`)

const UpdatePipelineAndAssignedSourcesMutationQL = gql(` 
  mutation UpdatePipelineAndAssignedSources($pipeline_id: String!, $nodes: [PartialSliceInput]!, $updateLoafSourcesInput: UpdateLoafSourcesInput!) {
    updateNodes(pipeline_id: $pipeline_id, nodes: $nodes) {
        id
        type
    }
    updateLoafSources(input: $updateLoafSourcesInput) {
      id
      assignedSources {
        id
        name
        type
      }
    }
  }
`)

const DeleteSlicesMutationQL = gql(`
  mutation DeleteSlices($slice_ids: [String]!) {
    deleteSlices(slice_ids: $slice_ids)
  }
`)

const DeleteLoafMutationQL = gql(`
  mutation DeleteLoaf($loafId: Int!) {
    deleteLoaf(loaf_id: $loafId)
  }
`)

const GetPipelineHistoryQL = gql(`
  query PipelineHistory($pipelineId: String!, $where: Boolean!, $limit: Int!, $page: Int!) {
    pipelineHistory(pipeline_id: $pipelineId, where: $where, limit: $limit, page: $page) {
      id
      nodeType
      sliceId
      errorMessage
      lastDay
      firstSeen
      lastSeen
      description
      timeseries {
        timestamp
        volume
      }
      records {
        record
        metadata {
          datatype
        }
      }
    }
  }
`)

const UpdateSliceStatusQueryQL = gql(`
  mutation UpdateSliceStatus($status: SliceStatusInput, $nodeId: String!) {
    updateSliceStatus(status: $status, node_id: $nodeId) {
      logsEnabled
      spansEnabled
      metricsEnabled
      logsStatus
      spansStatus
      metricsStatus
    }
  }
`)

export function LinechartAndPipelineQuery(pipelineParams: PipelineParams, queryParams: QueryParams) {
  return useQuery(LinechartAndPipelineQL, {
    variables: {
      queryParams: {
        endTime: queryParams.endTime,
        from: queryParams.from,
        limit: queryParams.limit || 100,
        offset: queryParams.offset || 0,
        startTime: queryParams.startTime,
        where: queryParams.where || '',
      },
      pipelineParams: {
        codeChain: pipelineParams.codeChain,
        currentNodeId: pipelineParams.currentNodeId,
        pipelineId: pipelineParams.pipelineId,
      },
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })
}

export function SimulateEventPipelineQuery(params: SimulateEventParams) {
  const {
    selectedNodeId,
    selectedEvent,
    selectedEventType,
    selectedNodeCode,
    events,
    nodes,
    edges,
    queryParams,
    skip,
  } = params
  let queryParameters
  if (queryParams) {
    queryParameters = {
      endTime: queryParams.endTime,
      from: queryParams.from,
      limit: queryParams.limit || 100,
      offset: queryParams.offset || 0,
      startTime: queryParams.startTime,
      where: queryParams.where || '',
    }
  }

  return useQuery(SimulateEventPipelineQL, {
    variables: {
      simulateEventParams: {
        selectedNodeId: selectedNodeId,
        selectedNodeCode: selectedNodeCode || '',
        selectedEvent: selectedEvent,
        selectedEventType: selectedEventType,
        events: events,
        nodes: nodes,
        edges: edges,
        queryParams: queryParameters,
      },
    },
    skip,
  })
}

export const SimulateEventPipelineLazyQuery = (): LazyQueryResultTuple<
  {
    pipeline: {
      simulateEvent: {
        events: { input: DatableLog | DatableTrace; output: DatableLog | DatableTrace }[]
        selectedEvent: {
          consoleLog: string[]
          errorLog: string[]
          input: DatableLog | DatableTrace
          output: DatableLog | DatableTrace
        }
      }
    }
  },
  { simulateEventParams: SimulateEventParams }
> =>
  useLazyQuery(SimulateEventPipelineQL, {
    fetchPolicy: 'network-only',
  })

export function GetPipelineListQuery() {
  return useQuery(GetPipelineListQL, {})
}

export function GetLoafQuery({ loafId }: { loafId: number }): QueryResult<{ getLoaf: Pipeline }, { loafId: number }> {
  return useQuery<{ getLoaf: Pipeline }, { loafId: number }>(GetLoafQL, {
    variables: { loafId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })
}

export function GetPipelineAndAssignedSourcesQuery(
  { loafId }: { loafId: number },
  skip: boolean
): QueryResult<{ getLoaf: Pipeline }, { loafId: number }> {
  return useQuery<{ getLoaf: Pipeline }, { loafId: number }>(GetLoafAndAssignedSourcesQL, {
    variables: { loafId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip,
  })
}

export function GetPipelineWithAssignedSourcesAndDestinationsQuery(
  { loafId }: { loafId: number },
  skip: boolean
): QueryResult<{ getLoaf: Pipeline }, { loafId: number }> {
  return useQuery<{ getLoaf: Pipeline }, { loafId: number }>(GetLoafWithAssignedSourcesAndDestinationsQL, {
    variables: { loafId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    skip,
  })
}

export function GetLoafLazyQuery({
  loafId,
}: {
  loafId: number
}): LazyQueryResultTuple<{ getLoaf: Pipeline }, { loafId: number }> {
  return useLazyQuery<{ getLoaf: Pipeline }, { loafId: number }>(GetLoafWithAssignedSourcesAndDestinationsQL, {
    variables: { loafId },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  })
}

export function CreatePipelineAndNodesQuery() {
  return useMutation(CreateLoafAndSlicesMutationQL, {})
}

export function CreatePipelineNodesAndAssignedSourcesQuery() {
  return useMutation(CreateLoafSlicesAndAssignedSourcesMutationQL)
}

export function UpdatePipelineAndAssignedSourcesQuery() {
  return useMutation(UpdatePipelineAndAssignedSourcesMutationQL)
}

export function DeleteNodesQuery() {
  return useMutation(DeleteSlicesMutationQL)
}

export function DeletePipelineQuery() {
  return useMutation(DeleteLoafMutationQL)
}

export function GetPipelineHistoryQuery(pipelineId: string, where: boolean, limit: number = 100, page: number = 1) {
  return useQuery(GetPipelineHistoryQL, {
    variables: {
      pipelineId,
      where,
      limit,
      page,
    },
  })
}

export const UpdateSliceStatusQuery = () => {
  return useMutation(UpdateSliceStatusQueryQL)
}
